import React, { useRef, useState } from 'react';
import { SubmitError, submitPublic } from '../lib/publicSubmit';
import Turnstile from '../components/Turnstile';
import { useSchool } from '../contexts/SchoolContext';
import './TrackApplication.css';

// "Track my application": reference number + the parent email used on the application. The answer is deliberately
// small (stage and last-update date) and the same "not found" message is shown whether the reference or the email
// is wrong. This is not a parent portal and needs no account.
export const STAGES = {
  received: { label: 'Received', text: 'We have received your application and it is waiting to be reviewed.' },
  under_review: { label: 'Under review', text: 'The admissions team is reviewing your application.' },
  information_required: {
    label: 'More information needed',
    text: 'The school needs more information or documents from you. Please contact the admissions office.'
  },
  interview: { label: 'Interview / assessment', text: 'Your application is at the interview or assessment stage. The school will contact you with the details.' },
  accepted: { label: 'Accepted', text: 'Your application has been accepted. Please contact the admissions office about the next steps.' },
  unsuccessful: { label: 'Not successful', text: 'This application was not successful. Please contact the admissions office if you have any questions.' },
  waiting_list: { label: 'Waiting list', text: 'Your application is on the waiting list. The school will contact you if a place becomes available.' },
  withdrawn: { label: 'Withdrawn', text: 'This application has been withdrawn.' }
};

const REFERENCE = /^KSMS-\d{4}-\d{6}$/i;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NOT_FOUND = 'We could not find an application with that reference number and email address. Please check both and try again, or contact the school.';

const formatDay = (ymd) => {
  const [y, m, d] = (ymd || '').split('-').map(Number);
  if (!y || !m || !d) return ymd || '';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

const TrackApplication = () => {
  const { settings } = useSchool();
  const [reference, setReference] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null); // { found: true, ... } | { found: false }
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState('');
  const [reset, setReset] = useState(0);
  const resultHeading = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    const found = {};
    if (!REFERENCE.test(reference.trim())) found.reference = 'Enter the reference number exactly as given, for example KSMS-2026-000123';
    if (!EMAIL.test(email.trim())) found.email = 'Enter the email address used on the application';
    setErrors(found);
    setResult(null);
    setMessage('');
    if (Object.keys(found).length) return;
    if (!token) {
      setMessage('Please complete the spam check and try again.');
      return;
    }
    setBusy(true);
    try {
      const data = await submitPublic({
        kind: 'track',
        payload: { reference_number: reference.trim().toUpperCase(), email: email.trim() },
        turnstileToken: token
      });
      setResult(data?.found ? data : { found: false });
      setTimeout(() => resultHeading.current?.focus(), 0);
    } catch (err) {
      setMessage(err instanceof SubmitError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
      setToken('');
      setReset((n) => n + 1);
    }
  };

  const stage = result?.found ? STAGES[result.stage] ?? STAGES.under_review : null;

  return (
    <div className="track-page">
      <section className="section">
        <div className="container track-container">
          <h1>Track my application</h1>
          <p>Enter the reference number from your confirmation (for example KSMS-2026-000123) and the email address you used on the application.</p>

          <form onSubmit={submit} className="track-form" noValidate>
            <div className="form-group">
              <label htmlFor="track-reference">Reference number</label>
              <input
                id="track-reference"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="KSMS-2026-000123"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck="false"
                maxLength={40}
                aria-invalid={errors.reference ? 'true' : undefined}
                aria-describedby={errors.reference ? 'track-reference-error' : undefined}
              />
              {errors.reference && <span id="track-reference-error" className="error-message">{errors.reference}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="track-email">Email address</label>
              <input
                id="track-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                maxLength={255}
                aria-invalid={errors.email ? 'true' : undefined}
                aria-describedby={errors.email ? 'track-email-error' : undefined}
              />
              {errors.email && <span id="track-email-error" className="error-message">{errors.email}</span>}
            </div>

            <Turnstile onToken={setToken} resetSignal={reset} />

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Checking…' : 'Check status'}
            </button>
          </form>

          {message && <div className="alert alert-error" role="alert">{message}</div>}

          <div aria-live="polite">
            {result && !result.found && (
              <div className="track-result not-found">
                <h2 ref={resultHeading} tabIndex={-1}>Application not found</h2>
                <p>{NOT_FOUND}</p>
              </div>
            )}
            {result?.found && (
              <div className="track-result found">
                <h2 ref={resultHeading} tabIndex={-1}>Application {result.reference_number}</h2>
                <p className="track-stage" data-testid="track-stage">{stage.label}</p>
                <p>{stage.text}</p>
                <p className="track-updated">Last updated: {formatDay(result.updated_on)}</p>
              </div>
            )}
          </div>

          {(result || message) && (
            <p className="track-contact">
              Questions? Contact the admissions office
              {settings.email_admissions && <> at <a href={`mailto:${settings.email_admissions}`}>{settings.email_admissions}</a></>}
              {settings.phones.length > 0 && (
                <> or call <a href={`tel:${settings.phones[0].tel}`}>{settings.phones[0].display}</a></>
              )}
              {' '}and quote your reference number.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackApplication;
